import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorService } from '../shared/services/error.service';

export interface ServiceOrderOpeningReason {
  id: number;
  statusName: string;
  configOrderId: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface ServiceOrderOpeningReasonCreate {
  statusName: string;
  configOrderId: number;
}

export interface ServiceOrderOpeningReasonUpdate {
  statusName?: string;
  configOrderId?: number;
}

@Injectable({ providedIn: 'root' })
export class ServiceOrderOpeningReasonsService {
  private readonly baseUrl = environment.microservices?.order || environment.apiUrl || '/api';
  private readonly resourcePath = 'serviceorderopeningreasons';

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    const headerMap: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headerMap['Authorization'] = `Bearer ${token}`;
    }

    return new HttpHeaders(headerMap);
  }

  private buildUrl(path: string): string {
    const [base, baseQuery] = String(this.baseUrl).split('?');
    const normalizedBase = base.replace(/\/+$/, '');
    const normalizedPath = String(path).replace(/^\/+/, '');
    let url = `${normalizedBase}/${normalizedPath}`;
    if (baseQuery) {
      url += `?${baseQuery}`;
    }
    return url;
  }

  /**
   * GET /serviceorderopeningreasons
   */
  getAll(): Observable<ServiceOrderOpeningReason[]> {
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders() }).pipe(
      map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao listar motivos de abertura de OS');
        return throwError(() => err);
      })
    );
  }

  /**
   * GET /serviceorderopeningreasons/{id}
   */
  getById(id: number): Observable<ServiceOrderOpeningReason> {
    return this.http.get<any>(this.buildUrl(`${this.resourcePath}/${id}`), { headers: this.getHeaders() }).pipe(
      map(res => this.mapFromDto(this.extractItem(res))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar motivo de abertura de OS');
        return throwError(() => err);
      })
    );
  }

  /**
   * GET /serviceorderopeningreasons?configOrderId={configOrderId}
   */
  getByConfigOrderId(configOrderId: number): Observable<ServiceOrderOpeningReason[]> {
    const params = new HttpParams().set('configOrderId', String(configOrderId));
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders(), params }).pipe(
      map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
      catchError(err => {
        // Alguns backends retornam 404 quando não há itens; tratar como lista vazia.
        if (err?.status === 404) {
          return of([] as ServiceOrderOpeningReason[]);
        }

        this.errorService.showErrorMessage('Erro ao listar motivos de abertura por configuração');
        return throwError(() => err);
      })
    );
  }

  /**
   * POST /serviceorderopeningreasons
   */
  create(payload: ServiceOrderOpeningReasonCreate): Observable<ServiceOrderOpeningReason> {
    const headers = this.getHeaders();

    return this.http.post<any>(this.buildUrl(this.resourcePath), payload, { headers }).pipe(
      map(res => this.mapFromDto(this.extractItem(res))),
      tap(() => this.errorService.showSuccessMessage('Motivo de abertura criado com sucesso')),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar motivo de abertura');
        return throwError(() => err);
      })
    );
  }

  /**
   * PUT /serviceorderopeningreasons/{id}
   */
  update(id: number, payload: ServiceOrderOpeningReasonUpdate): Observable<ServiceOrderOpeningReason> {
    const headers = this.getHeaders();

    return this.http.put<any>(this.buildUrl(`${this.resourcePath}/${id}`), payload, { headers }).pipe(
      map(res => this.mapFromDto(this.extractItem(res))),
      tap(() => this.errorService.showSuccessMessage('Motivo de abertura atualizado com sucesso')),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar motivo de abertura');
        return throwError(() => err);
      })
    );
  }

  /**
   * DELETE /serviceorderopeningreasons/{id}
   */
  delete(id: number): Observable<void> {
    return this.http.delete<void>(this.buildUrl(`${this.resourcePath}/${id}`), { headers: this.getHeaders() }).pipe(
      map(() => void 0),
      tap(() => this.errorService.showSuccessMessage('Motivo de abertura removido com sucesso')),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao remover motivo de abertura');
        return throwError(() => err);
      })
    );
  }

  private extractArray(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.data?.items)) return response.data.items;
    if (Array.isArray(response?.serviceOrderOpeningReasons)) return response.serviceOrderOpeningReasons;
    if (Array.isArray(response?.ServiceOrderOpeningReasons)) return response.ServiceOrderOpeningReasons;
    if (Array.isArray(response?.data?.serviceOrderOpeningReasons)) return response.data.serviceOrderOpeningReasons;
    if (Array.isArray(response?.data?.ServiceOrderOpeningReasons)) return response.data.ServiceOrderOpeningReasons;
    return [];
  }

  private extractItem(response: any): any {
    return response?.data?.data ?? response?.data ?? response?.item ?? response?.serviceOrderOpeningReason ?? response;
  }

  private mapFromDto(dto: any): ServiceOrderOpeningReason {
    return {
      id: dto?.Id ?? dto?.id ?? 0,
      statusName: dto?.StatusName ?? dto?.statusName ?? '',
      configOrderId: dto?.ConfigOrderId ?? dto?.configOrderId ?? null,
      createdAt: dto?.CreatedAt ?? dto?.createdAt ?? null,
      updatedAt: dto?.UpdatedAt ?? dto?.updatedAt ?? null
    };
  }
}
